import React from 'react';
import { ConfigProvider } from 'antd';
import { observer } from 'mobx-react';
import { applicationStore } from 'src/store/ApplicationStore';
import { mergeAntdTheme } from 'src/utils/clientThemeFromSettings';

/** Plato defaults — overridden when JWT `settings` is present on the access token. */
export const DEFAULT_ANTD_THEME = {
  token: {
    colorPrimary: '#CCB17B',
    colorLink: '#CCB17B',
    colorLinkActive: '#BC9553',
    colorLinkHover: '#BC9553',
    colorPrimaryActive: '#BC9553',
    colorPrimaryHover: '#DFCFA9',
    colorText: '#26273B',
    colorTextHeading: '#26273B',
    /**
     * Status renkleri — Plato sabit (JWT'den override edilmez). AntD `Alert`,
     * `message.success/error/warning/info` ve `notification` bileşenleri buradan beslenir.
     * Tailwind `flow.success`, `flow.danger` ile birebir.
     */
    colorSuccess: '#22C55E',
    colorError: '#FF6961',
    colorWarning: '#F59E0B',
    colorInfo: '#3B82F6',
    /** Kart / panel zemini — `surfaceColor` JWT alanı override eder */
    colorBgContainer: '#FFFFFF',
    colorBgElevated: '#FFFFFF',
    fontFamily: 'roc-grotesk',
    fontSizeSM: 13,
    borderRadius: 16,
  },
  components: {
    Table: {
      fontSize: 13,
    },
    Tag: {
      defaultBg: '#F0F0F5',
      colorBorder: '#F0F0F5',
    },
    TreeSelect: {
      nodeHoverBg: '#F8F5EE',
      nodeSelectedBg: '#F8F5EE',
    },
    Menu: {
      itemHoverBg: '#F0EFE9',
      itemSelectedBg: '#F0EFE9',
      itemActiveBg: '#F0EFE9',
      itemSelectedColor: '#CCB17B',
      iconSize: 16,
      horizontalItemHoverColor: '#ffffff',
    },
    Input: {
      borderRadius: 16,
      borderRadiusSM: 8,
      borderRadiusLG: 16,
      fontSizeLG: 16,
      fontSize: 16,
      controlHeight: 50,
      controlHeightLG: 50,
      paddingBlockLG: 14,
      paddingInlineLG: 16,
      colorBorder: '#D6D6E1',
      colorBgContainerDisabled: '#F0F0F5',
      colorTextDisabled: '#ACADC1',
      colorErrorBorder: '#FF6961',
      colorErrorBorderHover: '#FFA5A0',
      colorError: '#FF6961',
      colorBgContainer: '#FFFFFF',
      activeShadow: '0 0 0 2px rgba(204, 177, 123, 0.18)',
      errorActiveShadow: '0 0 0 2px rgba(255, 105, 97, 0.18)',
    },
    Button: {
      /**
       * Renkler: solid `primary` yazısı `colorTextLightSolid` ile gelir; white-label'de
       * `clientBranding.antdTheme.components.Button.primaryColor` override eder.
       * Boyutlar (52/44/48): `Button` wrapper varyantları `style.height` ile zorlar,
       * doğrudan antd Button kullanan çağrılar için aynı yükseklik token'larla sağlanır.
       */
      colorText: '#26273B',
      defaultColor: '#26273B',
      defaultBg: 'transparent',
      defaultBorderColor: '#26273B',
      borderRadius: 16,
      lineWidth: 2,
      defaultShadow: 'none',
      primaryShadow: 'none',
      dangerShadow: 'none',
      defaultHoverBg: '#CCB17B',
      defaultHoverColor: '#FCFCFD',
      defaultHoverBorderColor: '#CCB17B',
      defaultActiveBg: '#BC9553',
      defaultActiveColor: '#FCFCFD',
      defaultActiveBorderColor: '#BC9553',
      borderColorDisabled: '#ACADC1',
      colorPrimaryHover: '#BC9553',
      textHoverBg: '#F8F5EE',
      textTextHoverColor: '#BC9553',
      colorBgContainerDisabled: '#ACADC1',
      linkHoverDecoration: 'underline',
      colorTextDisabled: '#FFFFFF',
      controlHeight: 52,
      controlHeightSM: 44,
      controlHeightLG: 52,
      contentFontSizeSM: 13,
      paddingContentHorizontal: 10,
      borderRadiusSM: 16,
      borderRadiusLG: 16,
      fontSizeLG: 20,
      fontWeight: 600,
      fontSize: 16,
      fontFamily: 'roc-grotesk',
    },
    Pagination: {
      itemActiveBg: '',
    },
    DatePicker: {
      borderRadius: 8,
      borderRadiusLG: 16,
      controlHeight: 50,
      controlHeightLG: 50,
      colorBorder: '#D6D6E1',
      colorBgContainer: '#FFFFFF',
      colorBgContainerDisabled: '#F0F0F5',
      colorTextDisabled: '#ACADC1',
      colorErrorBorder: '#FF6961',
      activeShadow: '0 0 0 2px rgba(204, 177, 123, 0.18)',
      cellHoverBg: '#F8F5EE',
      cellActiveWithRangeBg: '#F8F5EE',
    },
    FloatButton: {
      colorPrimaryHover: '#BC9553',
      controlItemBgHover: '#F8F5EE',
    },
    Cascader: {
      controlItemBgHover: '#F8F5EE',
      controlItemBgActive: '#F8F5EE',
    },
    Dropdown: {
      colorLink: '#CCB17B',
      controlItemBgHover: '#F8F5EE',
    },
    Select: {
      controlHeightLG: 61,
      borderRadiusLG: 16,
      fontSize: 16,
      borderRadius: 16,
      controlHeight: 50,
      controlItemBgHover: '#F8F5EE',
      controlItemBgActive: '#F8F5EE',
      optionSelectedBg: '#F8F5EE',
      optionSelectedColor: '#26273B',
      colorBorder: '#D6D6E1',
      colorBgContainer: '#FFFFFF',
      selectorBg: '#FFFFFF',
      colorErrorBorder: '#FF6961',
      colorErrorBorderHover: '#FFA5A0',
    },
    Slider: {
      handleActiveColor: '#BC9553',
      trackBg: '#F8F5EE',
      trackHoverBg: '#DFCFA9',
    },
    Checkbox: {
      colorPrimaryHover: '#BC9553',
    },
    InputNumber: {
      colorPrimaryHover: '#CCB17B',
      colorPrimary: '#CCB17B',
      colorBgContainer: '#FFFFFF',
      borderRadiusSM: 8,
      borderRadiusLG: 16,
      fontSizeLG: 16,
      fontSize: 13,
      controlHeight: 40,
      controlHeightLG: 50,
    },
    Tabs: {
      itemHoverColor: '#DFCFA9',
      itemActiveColor: '#CCB17B',
    },
    Switch: { colorPrimaryHover: '#BC9553' },
    Spin: {
      colorPrimary: '#CCB17B',
    },
    Steps: {
      colorIcon: '#CCB17B',
      colorText: '#6F708B',
      colorPrimary: '#CCB17B',
    },
  },
};

const AntdConfigProvider = observer(({ children }) => {
  const theme = mergeAntdTheme(
    DEFAULT_ANTD_THEME,
    applicationStore.clientBranding?.antdTheme
  );

  return (
    <ConfigProvider theme={theme}>
      {children}
    </ConfigProvider>
  );
});

export default AntdConfigProvider;
